import { Building2, ChevronDown, LockKeyhole } from "lucide-react";
import { useContext, useEffect, useMemo, useRef } from "react";
import { DashboardRecordContext } from "@/pages/live/DashboardRecordContext";
import { storedDashboardTenantIds } from "@/lib/dashboard/scope";

import { useAppContext } from "@/contexts/AppContext";
import { useOptionalWorkspaceAudience } from "@/contexts/WorkspaceAudienceContext";
import { useOptionalAuthorization } from "@/contexts/AuthorizationContext";
import { useTenants } from "@/hooks/useTenants";
import { cn } from "@/lib/utils";
import type { Tenant } from "@/types";
import {
  Menu,
  MenuContent,
  MenuRadioGroup,
  MenuRadioItem,
  MenuSeparator,
  MenuTrigger,
  Skeleton,
  MultiSelect,
} from "@nexus/ui";

interface TenantScopeSelectorProps {
  className?: string;
  forcedAll?: boolean;
  coverageHint?: string;
  variant?: "scope" | "choice";
  appearance?: "neutral" | "highlighted";
  allowSingleOption?: boolean;
}

const ALL_TENANTS = "all";
const TENANT_CONTROL_SIZE = "h-10 w-60 max-w-full shrink-0";

export function TenantScopeSelector({
  className,
  forcedAll = false,
  coverageHint,
  appearance = "neutral",
}: TenantScopeSelectorProps) {
  const { selectedTenantIds, setSelectedTenantIds } = useAppContext();
  const audience = useOptionalWorkspaceAudience()?.audience;
  const isClientProfile = useOptionalAuthorization()?.isClientProfile ?? false;
  const { data: readableTenants = [], isLoading, isError } = useTenants();
  const dashboard = useContext(DashboardRecordContext);
  const tenants = useMemo(() => {
    const subset = storedDashboardTenantIds(dashboard?.config.scope);
    return subset
      ? readableTenants.filter((tenant) => subset.includes(tenant.tenant_id))
      : readableTenants;
  }, [readableTenants, dashboard]);

  const selectedTenantId = selectedTenantIds.length === 1 ? selectedTenantIds[0] : null;
  const selectedTenant = tenants.find((tenant) => tenant.tenant_id === selectedTenantId) ?? null;
  const singleTenant = tenants.length === 1 ? tenants[0] : null;

  useEffect(() => {
    if (forcedAll) return;
    if (isLoading || isError) return;

    if (singleTenant && selectedTenantId !== singleTenant.tenant_id) {
      setSelectedTenantIds([singleTenant.tenant_id]);
      return;
    }

    const validIds = selectedTenantIds.filter((id) =>
      tenants.some((tenant) => tenant.tenant_id === id),
    );
    if (validIds.length !== selectedTenantIds.length) {
      setSelectedTenantIds(validIds);
      return;
    }

    if (selectedTenantId && !selectedTenant) setSelectedTenantIds([]);
  }, [
    isError,
    isLoading,
    forcedAll,
    selectedTenant,
    selectedTenantId,
    selectedTenantIds.length,
    selectedTenantIds,
    tenants,
    setSelectedTenantIds,
    singleTenant,
  ]);

  if (audience?.kind === "client-demo") return null;
  if (isClientProfile && tenants.length < 2) return null;

  return (
    <TenantScopeControl
      className={className}
      forcedAll={forcedAll}
      coverageHint={coverageHint}
      appearance={appearance}
      tenants={tenants}
      selectedTenantIds={selectedTenantIds}
      onChange={setSelectedTenantIds}
      isLoading={isLoading}
      isError={isError}
    />
  );
}

export function TenantScopeControl({
  className,
  forcedAll = false,
  coverageHint,
  variant = "scope",
  appearance = "neutral",
  allowSingleOption = false,
  tenants,
  selectedTenantIds,
  onChange,
  isLoading = false,
  isError = false,
}: TenantScopeSelectorProps & {
  tenants: readonly Tenant[];
  selectedTenantIds: readonly string[];
  onChange: (tenantIds: string[]) => void;
  isLoading?: boolean;
  isError?: boolean;
}) {
  const pointerSelectionRef = useRef(false);
  const selectedTenantId = selectedTenantIds.length === 1 ? selectedTenantIds[0] : null;
  const selectedTenant = tenants.find((tenant) => tenant.tenant_id === selectedTenantId) ?? null;
  const effectiveTenant =
    selectedTenant ?? (variant === "scope" && tenants.length === 1 ? tenants[0] : null);
  const canSwitchScope = tenants.length > 1 || (allowSingleOption && tenants.length > 0);
  const isTenantScoped = effectiveTenant != null;
  const highlighted = isTenantScoped && variant === "scope" && appearance === "highlighted";

  if (forcedAll) {
    return (
      <div
        className={cn(
          TENANT_CONTROL_SIZE,
          "flex cursor-not-allowed items-center justify-between gap-2 rounded-lg border border-border/70 bg-surface-raised px-3 text-sm font-medium text-muted",
          className,
        )}
        aria-label="Tenant scope: All tenants"
        aria-disabled="true"
        title={coverageHint}
      >
        <span className="min-w-0 flex-1 truncate text-left">All tenants</span>
        <LockKeyhole className="h-3.5 w-3.5 shrink-0 text-subtle" aria-hidden />
      </div>
    );
  }

  // The pill keeps its size while tenants load: a placeholder stands in for the name.
  const label = isLoading ? (
    <Skeleton className="h-3 w-20" />
  ) : isError ? (
    "Unavailable"
  ) : (
    (effectiveTenant?.tenant_label ?? "All tenants")
  );

  if (!canSwitchScope) {
    return (
      <div
        className={cn(
          TENANT_CONTROL_SIZE,
          "flex items-center justify-center rounded-lg border px-3 text-sm font-medium shadow-sm shadow-black/3",
          highlighted
            ? "border-brand-cyan bg-brand-cyan text-white"
            : "border-border bg-surface text-primary",
          className,
        )}
        aria-label={
          effectiveTenant
            ? `Tenant scope: ${effectiveTenant.tenant_label}`
            : "Tenant scope unavailable"
        }
        title={coverageHint}
      >
        <span
          className="flex min-w-0 flex-1 items-center gap-2 text-left"
          title={coverageHint ?? "Tenant scope"}
        >
          <Building2 className="size-4 shrink-0 opacity-70" aria-hidden />
          <span className="min-w-0 truncate" title={effectiveTenant?.tenant_label}>
            {label}
          </span>
        </span>
      </div>
    );
  }

  if (variant === "scope") {
    const chosen = tenants.filter((tenant) => selectedTenantIds.includes(tenant.tenant_id));
    const summary =
      chosen.length === 0 || chosen.length === tenants.length
        ? "All tenants"
        : `${chosen
            .slice(0, 2)
            .map((tenant) => tenant.tenant_label)
            .join(", ")}${chosen.length > 2 ? "…" : ""}`;
    return (
      <MultiSelect
        label="Tenants"
        triggerLabel={`Tenant scope: ${summary}`}
        searchable
        allowEmpty={false}
        options={tenants.map((tenant) => ({
          value: tenant.tenant_id,
          label: tenant.tenant_label,
          group: tenant.customer ?? undefined,
        }))}
        value={
          selectedTenantIds.length === 0
            ? tenants.map((tenant) => tenant.tenant_id)
            : selectedTenantIds
        }
        onChange={(ids) => onChange(ids.length === tenants.length ? [] : ids)}
        allLabel="All tenants"
        emptyLabel="All tenants"
        disabled={isLoading || isError}
        className={cn(
          TENANT_CONTROL_SIZE,
          highlighted && "border-brand-cyan bg-brand-cyan text-white hover:bg-brand-cyan/90",
          className,
        )}
      />
    );
  }

  return (
    <Menu>
      <MenuTrigger asChild>
        <button
          type="button"
          className={cn(
            TENANT_CONTROL_SIZE,
            "group flex items-center justify-between gap-2 rounded-lg border px-3 text-sm font-medium shadow-sm shadow-black/3",
            "transition-[background-color,border-color,color] duration-240 ease-[cubic-bezier(0.25,1,0.5,1)] motion-reduce:transition-none",
            "focus:outline-none focus-visible:ring-2 focus-visible:ring-focus",
            highlighted
              ? "border-brand-cyan bg-brand-cyan text-white hover:bg-brand-cyan/90"
              : "border-border bg-surface text-primary hover:border-brand-cyan/50 hover:bg-surface-elevated",
            className,
          )}
          aria-label={`Tenant scope: ${effectiveTenant?.tenant_label ?? "All tenants"}`}
          title={coverageHint}
        >
          <span
            className="flex min-w-0 flex-1 items-center gap-2 text-left"
            title={coverageHint ?? "Tenant scope"}
          >
            <Building2 className="size-4 shrink-0 opacity-70" aria-hidden />
            <span className="min-w-0 truncate" title={effectiveTenant?.tenant_label}>
              {label}
            </span>
          </span>
          <ChevronDown
            className={cn(
              "h-3.5 w-3.5 shrink-0 transition-transform duration-150 group-data-[state=open]:rotate-180 motion-reduce:transition-none",
              highlighted ? "text-white/80" : "text-muted",
            )}
            aria-hidden
          />
        </button>
      </MenuTrigger>

      <MenuContent
        align="end"
        collisionPadding={8}
        className="w-60 max-w-[calc(100vw-1rem)]"
        onPointerDownCapture={() => {
          pointerSelectionRef.current = true;
        }}
        onCloseAutoFocus={(event) => {
          if (!pointerSelectionRef.current) return;
          event.preventDefault();
          pointerSelectionRef.current = false;
        }}
      >
        <MenuRadioGroup
          value={selectedTenant?.tenant_id ?? ALL_TENANTS}
          onValueChange={(value) => onChange(value === ALL_TENANTS ? [] : [value])}
        >
          <MenuRadioItem
            value={ALL_TENANTS}
            className={cn("h-auto py-2", isTenantScoped && "bg-surface-raised")}
          >
            <span className="font-medium">All tenants</span>
          </MenuRadioItem>
          <MenuSeparator />
          {tenants.map((tenant) => (
            <MenuRadioItem key={tenant.tenant_id} value={tenant.tenant_id}>
              <span className="truncate">{tenant.tenant_label}</span>
            </MenuRadioItem>
          ))}
        </MenuRadioGroup>
      </MenuContent>
    </Menu>
  );
}
